import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Form, 
  Button, 
  Table, 
  Badge, 
  Modal, 
  Alert, 
  Row, 
  Col, 
  InputGroup,
  Nav,
  Tab
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faKey, 
  faShieldAlt, 
  faUsers, 
  faEye, 
  faEyeSlash, 
  faCheckCircle, 
  faExclamationTriangle,
  faLock,
  faBell,
  faPlus,
  faEdit,
  faTrash,
  faSpinner,
  faDownload,
  faBuilding,
  faAddressBook
} from '@fortawesome/free-solid-svg-icons';
import { ProgressBar } from 'react-bootstrap';
import axiosInstance from 'helpers/api';

interface APICredential {
  id: string;
  service_type: string;
  service_name: string;
  credential_format: string;
  subdomain?: string;
  custom_endpoint?: string;
  is_active: boolean;
  is_validated: boolean;
  last_validated_at?: string;
  validation_error_message?: string;
  last_used_at?: string;
  usage_count: number;
  created_at: string;
  service_config?: {
    name: string;
    auth_type: string;
    required_fields: string[];
    documentation_url: string;
    data_migration_capabilities: string[];
  };
}

interface SecuritySettingsProps {
  userId?: string;
}

interface ExtractionStatus {
  status: 'idle' | 'processing' | 'completed' | 'failed';
  current?: number;
  total?: number;
  percentage?: number;
  message?: string;
  timestamp?: string;
  statistics?: {
    clients_processed?: number;
    total_notes?: number;
    total_events?: number;
    total_deals?: number;
  };
}

interface ServiceGuidanceProps {
  onAddCredential: () => void;
  credential: APICredential | null;
  serviceConfig: any;
}

// Add service configurations at the top level
const serviceConfigurations = {
  'crm_followup_boss': {
    displayName: 'FollowUp Boss',
    description: 'Sync leads, contacts, and deals with your FollowUp Boss CRM',
    icon: faUsers,
    color: 'info',
    documentationUrl: 'https://developers.followupboss.com/',
    extractionCapabilities: ['leads', 'contacts', 'notes', 'calls', 'events', 'deals']
  },
  'followup_boss': {
    displayName: 'FollowUp Boss',
    description: 'Sync leads, contacts, and deals with your FollowUp Boss CRM',
    icon: faUsers,
    color: 'info',
    documentationUrl: 'https://developers.followupboss.com/',
    extractionCapabilities: ['leads', 'contacts', 'notes', 'calls', 'events', 'deals']
  },
  'crm_kv_core': {
    displayName: 'KVCore',
    description: 'Sync leads, contacts, and properties with your KVCore CRM',
    icon: faBuilding,
    color: 'primary',
    documentationUrl: 'https://kvcore.com/api-docs/',
    extractionCapabilities: ['contacts', 'leads', 'properties', 'showings', 'activities']
  },
  'kv_core': {
    displayName: 'KVCore',
    description: 'Sync leads, contacts, and properties with your KVCore CRM',
    icon: faBuilding,
    color: 'primary',
    documentationUrl: 'https://kvcore.com/api-docs/',
    extractionCapabilities: ['contacts', 'leads', 'properties', 'showings', 'activities']
  },
  'crm_wise_agent': {
    displayName: 'Wise Agent',
    description: 'Import contacts, leads, and tasks from your Wise Agent CRM',
    icon: faAddressBook,
    color: 'success',
    documentationUrl: 'https://developers.wiseagent.com/',
    extractionCapabilities: ['contacts', 'leads', 'tasks', 'notes']
  },
  'wise_agent': {
    displayName: 'Wise Agent',
    description: 'Import contacts, leads, and tasks from your Wise Agent CRM',
    icon: faAddressBook,
    color: 'success',
    documentationUrl: 'https://developers.wiseagent.com/',
    extractionCapabilities: ['contacts', 'leads', 'tasks', 'notes']
  }
};

const ServiceGuidance: React.FC<ServiceGuidanceProps> = ({ 
  onAddCredential, 
  credential,
  serviceConfig
}) => {
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>({ status: 'idle' });
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Fetch extraction status for this specific service
  const fetchExtractionStatus = useCallback(async () => {
    if (!credential?.id || !credential.is_validated) return;

    try {
      const response = await axiosInstance.get('/api-credentials/extraction-progress');
      const extractions = response.data.extractions || [];
      
      // Find active extraction for this credential
      const activeExtraction = extractions.find((e: any) => 
        e.service === credential.service_type && e.status === 'processing'
      );

      if (activeExtraction) {
        setExtractionStatus({
          status: 'processing',
          current: activeExtraction.current,
          total: activeExtraction.total,
          percentage: activeExtraction.percentage,
          message: activeExtraction.message,
          timestamp: activeExtraction.timestamp
        });
      } else if (extractionStatus.status === 'processing') {
        // Was processing, now complete
        setExtractionStatus({
          status: 'completed',
          message: 'Data extraction completed successfully!',
          statistics: activeExtraction?.statistics
        });
        
        // Stop polling after showing completion for 30 seconds
        setTimeout(() => {
          setExtractionStatus({ status: 'idle' });
        }, 30000);
      }
    } catch (error) {
      console.error('Failed to fetch extraction status:', error);
    }
  }, [credential?.id, credential?.is_validated, credential?.service_type, extractionStatus.status]);

  // Poll for extraction status when processing
  useEffect(() => {
    if (extractionStatus.status === 'processing') {
      const interval = setInterval(fetchExtractionStatus, 3000);
      setPollingInterval(interval);
      return () => clearInterval(interval);
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [extractionStatus.status, fetchExtractionStatus, pollingInterval]);

  // Check for active extraction on mount
  useEffect(() => {
    if (credential?.is_validated) {
      fetchExtractionStatus();
    }
  }, [credential?.is_validated, fetchExtractionStatus]);

  const getStatusColor = () => {
    if (extractionStatus.status === 'processing') return 'primary';
    if (extractionStatus.status === 'completed') return 'success';
    if (extractionStatus.status === 'failed') return 'danger';
    
    if (!credential) return 'secondary';
    if (credential.is_validated) return 'success';
    return 'warning';
  };

  const getStatusText = () => {
    if (extractionStatus.status === 'processing') return 'Extracting Data...';
    if (extractionStatus.status === 'completed') return 'Extraction Complete';
    if (extractionStatus.status === 'failed') return 'Extraction Failed';
    
    if (!credential) return 'Not Connected';
    if (credential.is_validated) return 'Connected & Ready';
    return 'Connected - Needs Validation';
  };

  const getGuidanceMessage = () => {
    if (!credential) {
      return {
        variant: 'info' as const,
        icon: faShieldAlt,
        title: 'Ready to Connect:',
        message: `Add your ${serviceConfig.displayName} credentials to start syncing data automatically.`
      };
    }
    
    if (!credential.is_validated && extractionStatus.status === 'idle') {
      return {
        variant: 'success' as const,
        icon: faCheckCircle,
        title: `${serviceConfig.displayName} API Successfully Added!`,
        message: 'Click the validate button (✓) below to confirm access and start data extraction.'
      };
    }
    
    if (credential.is_validated && extractionStatus.status === 'idle') {
      return {
        variant: 'success' as const,
        icon: faCheckCircle,
        title: 'Connected & Ready:',
        message: `Your ${serviceConfig.displayName} account is connected and data has been synced.`
      };
    }
    
    return null;
  };

  const guidance = getGuidanceMessage();

  return (
    <Card className={`mb-4 border-${serviceConfig.color}`}>
      <Card.Header className={`bg-${serviceConfig.color} text-white`}>
        <FontAwesomeIcon icon={serviceConfig.icon} className="me-2" />
        {serviceConfig.displayName} Integration
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={12}>
            <div className="d-flex align-items-center mb-3">
              <div className="me-3">
                <div 
                  className={`d-flex align-items-center justify-content-center rounded bg-${serviceConfig.color}`}
                  style={{ width: '40px', height: '40px' }}
                >
                  <FontAwesomeIcon icon={serviceConfig.icon} className="text-white" />
                </div>
              </div>
              <div className="flex-grow-1">
                <h6 className="mb-0">{serviceConfig.displayName} CRM</h6>
                <small className="text-muted">{serviceConfig.description}</small>
              </div>
            </div>

            {/* Status Badge and Progress */}
            <div className="mb-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Badge bg={getStatusColor()} className="me-2">
                  {extractionStatus.status === 'processing' && (
                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                  )}
                  {getStatusText()}
                </Badge>
                {extractionStatus.status === 'processing' && extractionStatus.percentage !== undefined && (
                  <small className="text-muted">
                    {Number(extractionStatus.percentage).toFixed(1)}% complete
                  </small>
                )}
              </div>

              {/* Progress Bar */}
              {extractionStatus.status === 'processing' && extractionStatus.percentage !== undefined && (
                <div className="mb-3">
                  <ProgressBar 
                    now={Number(extractionStatus.percentage)} 
                    animated 
                    striped 
                    variant="primary"
                    label={`${extractionStatus.current || 0} of ${extractionStatus.total || 0} contacts`}
                    style={{ height: '25px', fontSize: '14px' }}
                  />
                  <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                      {extractionStatus.message || 'Processing...'}
                    </small>
                  </div>
                </div>
              )}

              {/* Completion Statistics */}
              {extractionStatus.status === 'completed' && extractionStatus.statistics && (
                <Alert variant="success" className="mb-3">
                  <h6 className="alert-heading">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Extraction Complete!
                  </h6>
                  <Row className="mt-2">
                    {serviceConfig.extractionCapabilities.slice(0, 4).map((capability: string, index: number) => (
                      <Col key={capability} xs={6} md={3}>
                        <small className="d-block text-muted">{capability.charAt(0).toUpperCase() + capability.slice(1)}</small>
                        <strong>{(extractionStatus.statistics as any)?.[`total_${capability}`] || 0}</strong>
                      </Col>
                    ))}
                  </Row>
                </Alert>
              )}

              {/* Guidance Messages */}
              {guidance && (
                <Alert variant={guidance.variant} className="mb-3">
                  <FontAwesomeIcon icon={guidance.icon} className="me-2" />
                  <strong>{guidance.title}</strong> {guidance.message}
                </Alert>
              )}
            </div>
          </Col>
        </Row>
        
        {/* Compact Security Note */}
        <div className="d-flex align-items-center p-2 bg-light rounded">
          <FontAwesomeIcon icon={faShieldAlt} className="text-muted me-2" size="sm" />
          <small className="text-muted mb-0">
            Your API key is encrypted and stored securely. We only use it to sync your CRM data.
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

const SecuritySettings: React.FC<SecuritySettingsProps> = ({ userId }) => {
  // Component state
  const [activeTab, setActiveTab] = useState('api-credentials');
  const [credentials, setCredentials] = useState<APICredential[]>([]);
  const [availableServices, setAvailableServices] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<APICredential | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    service_type: '',
    service_name: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    username: '',
    password: '',
    subdomain: '',
    custom_endpoint: '',
    credential_format: 'api_key'
  });

  // Security preferences
  const [securityPrefs, setSecurityPrefs] = useState({
    two_factor_enabled: false,
    email_notifications: true,
    sms_notifications: false,
    login_alerts: true,
    api_notifications: false,
    data_retention_days: 365,
    auto_logout_minutes: 60
  });

  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});

  // Fetch credentials
  const fetchCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      
      const response = await axiosInstance.get('/api-credentials');
      
      if (response.data.success) {
        setCredentials(response.data.data.credentials || []);
        setAvailableServices(response.data.data.available_services || {});
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Session expired. Please refresh the page and log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to load API credentials');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch credentials on component mount
  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleAddCredential = () => {
    setFormData({
      service_type: '',
      service_name: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      username: '',
      password: '',
      subdomain: '',
      custom_endpoint: '',
      credential_format: 'api_key'
    });
    setShowAddModal(true);
  };

  const handleEditCredential = (credential: APICredential) => {
    setFormData({
      service_type: credential.service_type,
      service_name: credential.service_name,
      api_key: '',
      api_secret: '',
      access_token: '',
      username: '',
      password: '',
      subdomain: credential.subdomain || '',
      custom_endpoint: credential.custom_endpoint || '',
      credential_format: credential.credential_format
    });
    setSelectedCredential(credential);
    setShowAddModal(true);
  };

  const handleServiceTypeChange = (serviceType: string) => {
    const serviceConfig = availableServices[serviceType];
    if (serviceConfig) {
      setFormData(prev => ({
        ...prev,
        service_type: serviceType,
        service_name: serviceConfig.name,
        credential_format: serviceConfig.auth_type
      }));
    }
  };

  const submitCredential = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.service_type || !formData.service_name) {
        throw new Error('Service type and name are required');
      }

      const serviceConfig = availableServices[formData.service_type];
      if (serviceConfig?.required_fields) {
        for (const field of serviceConfig.required_fields) {
          if (!formData[field as keyof typeof formData]) {
            throw new Error(`${field.replace('_', ' ')} is required for ${serviceConfig.name}`);
          }
        }
      }

      const response = await axiosInstance.post('/api-credentials', formData);
      
      if (response.data.success) {
        setSuccess(`API credential ${selectedCredential ? 'updated' : 'added'} successfully`);
        setShowAddModal(false);
        setSelectedCredential(null);
        fetchCredentials();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save credential');
    } finally {
      setLoading(false);
    }
  };

  const validateCredential = async (credentialId: string) => {
    try {
      setLoading(true);
      setError(null);

      const credential = credentials.find(c => c.id === credentialId);
      if (!credential) {
        setError('Credential not found.');
        return;
      }

      const result = await axiosInstance.post(`/api-credentials/${credential.id}/validate`);
      
      if (result.data.success) {
        // Update credential status
        const updatedCredentials = credentials.map(cred => 
          cred.id === credential.id 
            ? { ...cred, is_validated: true, validation_error_message: undefined }
            : cred
        );
        setCredentials(updatedCredentials);
        setSuccess('Credential validated successfully!');
        
        // If this is Follow-up Boss, validation automatically triggers extraction
        if (credential.service_type === 'crm_followup_boss' || credential.service_type === 'followup_boss') {
          setTimeout(() => {
            setSuccess('Follow-Up Boss data extraction started! This may take up to 2 hours for large datasets.');
          }, 2000);
        }

        // If this is Wise Agent, validation automatically triggers import
        if (credential.service_type === 'crm_wise_agent' || credential.service_type === 'wise_agent') {
          setTimeout(() => {
            setSuccess('Wise Agent contact import started! Your leads will be available shortly.');
          }, 2000);
        }
      } else {
        setError(result.data.message || 'Failed to validate credential');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate credential');
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async () => {
    if (!selectedCredential) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.delete(`/api-credentials/${selectedCredential.id}`);
      
      if (response.data.success) {
        setSuccess('API credential deleted successfully');
        setShowDeleteModal(false);
        setSelectedCredential(null);
        fetchCredentials();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete credential');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const renderCredentialForm = () => (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Service Type *</Form.Label>
            <Form.Select 
              value={formData.service_type} 
              onChange={(e) => handleServiceTypeChange(e.target.value)}
              required
            >
              <option value="">Select a service</option>
              {Object.entries(availableServices).map(([key, service]: [string, any]) => (
                <option key={key} value={key}>{service.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Service Name *</Form.Label>
            <Form.Control 
              type="text" 
              value={formData.service_name} 
              onChange={(e) => setFormData(prev => ({ ...prev, service_name: e.target.value }))}
              placeholder="e.g., Main CRM Account"
              required
            />
          </Form.Group>
        </Col>
      </Row>

      {formData.service_type && availableServices[formData.service_type] && (
        <>
          {availableServices[formData.service_type].required_fields?.includes('api_key') && (
            <Form.Group className="mb-3">
              <Form.Label>API Key *</Form.Label>
              <InputGroup>
                <Form.Control 
                  type={showPasswords.api_key ? 'text' : 'password'}
                  value={formData.api_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your API key"
                  required
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => togglePasswordVisibility('api_key')}
                >
                  <FontAwesomeIcon icon={showPasswords.api_key ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>
          )}

          {availableServices[formData.service_type].required_fields?.includes('subdomain') && (
            <Form.Group className="mb-3">
              <Form.Label>Subdomain</Form.Label>
              <Form.Control 
                type="text" 
                value={formData.subdomain}
                onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                placeholder="e.g., yourcompany"
              />
            </Form.Group>
          )}

          <Alert variant="info">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
            <strong>Documentation:</strong>{' '}
            <a 
              href={availableServices[formData.service_type].documentation_url} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              How to get your {availableServices[formData.service_type].name} API credentials
            </a>
          </Alert>
        </>
      )}
    </Form>
  );

  const renderCredentialsTab = () => {
    // Get unique services that have credentials
    const servicesWithCredentials = credentials.reduce((acc, credential) => {
      const serviceConfig = (serviceConfigurations as any)[credential.service_type];
      if (serviceConfig && !acc.find(item => item.serviceType === credential.service_type)) {
        acc.push({
          serviceType: credential.service_type,
          serviceConfig: serviceConfig,
          credential: credential
        });
      }
      return acc;
    }, [] as Array<{serviceType: string, serviceConfig: any, credential: APICredential}>);

    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            API Credentials
          </h5>
          <Button variant="primary" onClick={handleAddCredential}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Credential
          </Button>
        </div>

        {/* Only show service guidance for services that have credentials */}
        {servicesWithCredentials.map(({ serviceType, serviceConfig, credential }) => (
          <ServiceGuidance 
            key={serviceType}
            onAddCredential={handleAddCredential}
            credential={credential}
            serviceConfig={serviceConfig}
          />
        ))}

        {credentials.length === 0 ? (
          <Alert variant="info">
            <FontAwesomeIcon icon={faKey} className="me-2" />
            No API credentials configured. Add your first credential to start syncing data.
          </Alert>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Last Validated</th>
                <th>Usage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map((credential) => (
                <tr key={credential.id}>
                  <td>
                    <div>
                      <strong>{credential.service_name}</strong>
                      <br />
                      <small className="text-muted">{credential.service_type}</small>
                    </div>
                  </td>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <Badge 
                        bg={credential.is_validated ? 'success' : 'warning'} 
                        className="text-center"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        {credential.is_validated ? (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" size="xs" />
                            Validated
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" size="xs" />
                            Not Validated
                          </>
                        )}
                      </Badge>
                      <Badge 
                        bg={credential.is_active ? 'success' : 'secondary'} 
                        className="text-center"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', opacity: credential.is_active ? 1 : 0.7 }}
                      >
                        {credential.is_active ? (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" size="xs" />
                            Active
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" size="xs" />
                            Inactive
                          </>
                        )}
                      </Badge>
                    </div>
                  </td>
                  <td>
                    <small className="text-muted">
                      {credential.last_validated_at 
                        ? new Date(credential.last_validated_at).toLocaleDateString()
                        : 'Never'
                      }
                    </small>
                  </td>
                  <td>
                    <small className="text-muted">
                      {credential.usage_count || 0} requests
                    </small>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleEditCredential(credential)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => validateCredential(credential.id)}
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          setSelectedCredential(credential);
                          setShowDeleteModal(true);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </>
    );
  };

  const renderSecurityTab = () => (
    <div>
      <h5 className="mb-4">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
        Account Security
      </h5>
      
      {/* Password Management */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faLock} className="me-2" />
          Password & Authentication
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password *</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showPasswords.current_password ? 'text' : 'password'} 
                    placeholder="Enter current password" 
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('current_password')}
                  >
                    <FontAwesomeIcon icon={showPasswords.current_password ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>New Password *</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showPasswords.new_password ? 'text' : 'password'} 
                    placeholder="Enter new password" 
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('new_password')}
                  >
                    <FontAwesomeIcon icon={showPasswords.new_password ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 8 characters with uppercase, lowercase, and number
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center">
            <Button variant="primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
            <small className="text-muted">
              Last updated: Never
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Security Notifications */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faBell} className="me-2" />
          Security Notifications
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="email-notifications"
                label="Email notifications for security alerts"
                checked={securityPrefs.email_notifications}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  email_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="login-alerts"
                label="Login alerts from new devices"
                checked={securityPrefs.login_alerts}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  login_alerts: e.target.checked 
                }))}
                className="mb-3"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="sms-notifications"
                label="SMS notifications for critical alerts"
                checked={securityPrefs.sms_notifications}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  sms_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="api-notifications"
                label="API credential usage alerts"
                checked={securityPrefs.api_notifications || false}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  api_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Save Changes Button */}
      <div className="d-flex justify-content-end">
        <Button variant="primary" disabled={loading}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {loading ? 'Saving...' : 'Save Security Settings'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="security-settings">
      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
          {error && (error?.includes('Session expired') || error?.includes('Authentication required')) && (
            <div className="mt-2">
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={() => window.location.reload()}
                className="me-2"
              >
                Refresh Page
              </Button>
              <small className="text-muted">
                Or try logging out and logging back in if the issue persists.
              </small>
            </div>
          )}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'api-credentials')}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="api-credentials">
              <FontAwesomeIcon icon={faKey} className="me-2" />
              API Credentials
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="security">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Account Security
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="privacy">
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Privacy Settings
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="api-credentials">
            {renderCredentialsTab()}
          </Tab.Pane>
          <Tab.Pane eventKey="security">
            {renderSecurityTab()}
          </Tab.Pane>
          <Tab.Pane eventKey="privacy">
            <div>
              <h5>Privacy Settings</h5>
              <p>Privacy controls coming soon...</p>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Add/Edit Credential Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCredential ? 'Edit API Credential' : 'Add API Credential'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCredentialForm()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitCredential} disabled={loading}>
            {loading ? 'Saving...' : (selectedCredential ? 'Update' : 'Add')} Credential
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
            <h5>Are you sure?</h5>
            <p>
              This will permanently delete the API credential for{' '}
              <strong>{selectedCredential?.service_name}</strong>.
            </p>
            <p className="text-danger">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteCredential} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Credential'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SecuritySettings; 